import { useCallback, useMemo } from 'react';

import { type ViewerImage, type ViewerVideo } from '@/components/media';
import { type DeliveryGalleryItem } from '@/services/deliveries';
import { toViewerImage, toViewerVideo } from '@/utils/deliveryFormat';

import { useMediaViewer } from './useMediaViewer';

type Options = {
  /** Whatever is on screen, in the order it is on screen. */
  items: readonly DeliveryGalleryItem[];
  /** The one call to action drawn inside both viewers. */
  action?: { label: string; to: string } | undefined;
};

export type DeliveryViewers = {
  /** Hand this to every tile. It works out which viewer the medium belongs in. */
  openItem: (item: DeliveryGalleryItem) => void;
  close: () => void;
  imagesOpen: boolean;
  videosOpen: boolean;
  /** Each viewer tracks its own position; see `useMediaViewer`. */
  imageIndex: number;
  videoIndex: number;
  setImageIndex: (index: number) => void;
  setVideoIndex: (index: number) => void;
  images: ViewerImage[];
  videos: ViewerVideo[];
};

/**
 * Turns whatever is on screen into the two viewer stacks, and decides which
 * one a tap opens.
 *
 * ## The stacks follow the filters
 *
 * They are built from the items actually rendered, not from the whole archive.
 * So a visitor who has filtered to Bunagana and opens the second photograph
 * pages through Bunagana's photographs — the viewer agrees with the grid it
 * was opened from, which is the only behaviour that is not surprising. It also
 * means a proof strip of six tiles opens a viewer of six, not of ninety.
 *
 * ## Photographs and clips are separate stacks
 *
 * A left-right lightbox and a vertical feed are different gestures, and a
 * single stack would make "next" mean a swipe sideways for one item and
 * upwards for the next. So the two are split here, once, and a tile only has
 * to say which item it is — it never has to know which viewer that implies.
 *
 * The position is looked up by id rather than passed down as an index, so no
 * component has to carry a number whose meaning depends on a filter.
 */
export function useDeliveryViewers({ items, action }: Options): DeliveryViewers {
  const viewer = useMediaViewer();

  const images = useMemo(
    () =>
      items.filter((item) => item.media.kind === 'image').map((item) => toViewerImage(item, action)),
    [items, action],
  );

  const videos = useMemo(
    () =>
      items.filter((item) => item.media.kind === 'video').map((item) => toViewerVideo(item, action)),
    [items, action],
  );

  const openItem = useCallback(
    (item: DeliveryGalleryItem) => {
      if (item.media.kind === 'video') {
        const index = videos.findIndex((video) => video.id === item.media.id);
        viewer.openVideos(Math.max(index, 0));
        return;
      }
      const index = images.findIndex((image) => image.id === item.media.id);
      viewer.openImages(Math.max(index, 0));
    },
    [images, videos, viewer],
  );

  return {
    openItem,
    close: viewer.close,
    imagesOpen: viewer.imagesOpen,
    videosOpen: viewer.videosOpen,
    imageIndex: viewer.imageIndex,
    videoIndex: viewer.videoIndex,
    setImageIndex: viewer.setImageIndex,
    setVideoIndex: viewer.setVideoIndex,
    images,
    videos,
  };
}
